var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import "./positioner.css";
import { useBEM } from '../../utils';
var _a = useBEM('lk-ui__positioner'), positionerBlock = _a[0], positionerModifier = _a[1];
var defaultMinHeight = 88;
export var Positioner = function (_a) {
    var _b;
    var children = _a.children, _c = _a.align, align = _c === void 0 ? 'center' : _c, targetElement = _a.targetElement, _d = _a.verticalOffset, verticalOffset = _d === void 0 ? 0 : _d, _e = _a.horizontalOffset, horizontalOffset = _e === void 0 ? 0 : _e, _f = _a.applicationRootElementID, applicationRootElementID = _f === void 0 ? 'root' : _f, scrollable = _a.scrollable, _g = _a.minHeight, minHeight = _g === void 0 ? defaultMinHeight : _g;
    var positionerRef = useRef(null);
    var _h = useState(false), hasCalculatedStyle = _h[0], setCalculatedStyle = _h[1];
    var _j = useState(document.getElementById(applicationRootElementID) || document.body), root = _j[0], setRoot = _j[1];
    useLayoutEffect(function () {
        var rootElement = document.getElementById(applicationRootElementID);
        if (!rootElement)
            throw new Error("Application root element with ID \"" + applicationRootElementID + "\" not found");
        setRoot(rootElement);
    }, [applicationRootElementID]);
    var applyStyle = useCallback(function (_a) {
        var position = _a.position, pixelValues = __rest(_a, ["position"]);
        if (!positionerRef.current)
            return;
        var positionString = position ? "position:" + position + ";" : '';
        var props = [
            'top',
            'left',
            'right',
            'bottom',
            'height',
        ];
        var sideValuesString = props
            .map(function (key) {
            return pixelValues[key] !== undefined
                ? key + ":" + pixelValues[key] + "px;"
                : key + ":auto;";
        })
            .join('');
        positionerRef.current.setAttribute('style', positionString + sideValuesString);
        if (!hasCalculatedStyle) {
            setCalculatedStyle(true);
        }
    }, [hasCalculatedStyle]);
    var updatePosition = useCallback(function () {
        if (!positionerRef.current || !targetElement.current) {
            return;
        }
        var style = {};
        var targetElRect = targetElement.current.getBoundingClientRect();
        var bodyRect = document.body.getBoundingClientRect();
        var positionerRect = positionerRef.current.getBoundingClientRect();
        var positionerScrollHeight = positionerRef.current.scrollHeight;
        if (
        /**
         * Если снизу хватает места.
         */
        positionerScrollHeight <
            bodyRect.height - targetElRect.bottom + verticalOffset) {
            style.top = targetElRect.bottom + verticalOffset;
        }
        else if (
        /**
         * Если сверху хватает места.
         */
        positionerScrollHeight <
            targetElRect.top - verticalOffset) {
            style.top = targetElRect.top - verticalOffset - positionerRect.height;
        }
        else if (
        /**
         * Если сверху больше места, чем снизу и
         * это место больше минимальной высоты и
         * дропдоун может скроллиться
         */
        bodyRect.height - targetElRect.bottom + verticalOffset <
            targetElRect.top - verticalOffset &&
            targetElRect.top - verticalOffset > minHeight &&
            scrollable) {
            style.top = verticalOffset;
            style.height = targetElRect.top - verticalOffset * 2;
        }
        else {
            /**
             * Иначе, прикрепляем книзу страницы
             */
            style.top = targetElRect.bottom + verticalOffset;
            if (scrollable) {
                style.height = Math.max(bodyRect.height - targetElRect.bottom - verticalOffset * 2, minHeight);
            }
        }
        if (align === 'center') {
            /**
             * Выравнивание по центру или по ближайшему краю страницы
             */
            style.left = Math.min(targetElRect.left + targetElRect.width / 2 - positionerRect.width / 2, bodyRect.width - positionerRect.width - horizontalOffset);
            if (style.left < horizontalOffset) {
                style.left = horizontalOffset;
            }
        }
        else if (align === 'right') {
            /**
             * Выравнивание по правому краю
             */
            style.left = Math.max(targetElRect.right - positionerRect.width, horizontalOffset);
        }
        else {
            /**
             * Выравнивание по левому краю
             */
            style.left = Math.min(targetElRect.left, bodyRect.width - positionerRect.width - horizontalOffset);
        }
        applyStyle(style);
    }, [align, applyStyle, horizontalOffset, targetElement, verticalOffset]);
    useLayoutEffect(function () {
        updatePosition();
        var disposables = [];
        var handleListener = function (element, type) {
            element.addEventListener(type, updatePosition);
            disposables.push(function () { return element.removeEventListener(type, updatePosition); });
        };
        handleListener(window, 'resize');
        handleListener(window, 'scroll');
        var appRootElement = root.parentElement;
        var currentElement = targetElement.current
            ? targetElement.current
            : appRootElement;
        var checkForScrollbar = function (element) {
            return element.scrollHeight !== element.clientHeight ||
                element.scrollWidth !== element.clientWidth;
        };
        while (currentElement) {
            checkForScrollbar(currentElement) &&
                handleListener(currentElement, 'scroll');
            if (currentElement === appRootElement)
                break;
            if (currentElement.parentNode instanceof ShadowRoot) {
                currentElement = currentElement.parentNode.host;
            }
            else {
                currentElement = currentElement.parentNode;
            }
        }
        return function () {
            disposables.forEach(function (cleanup) { return cleanup(); });
        };
    }, [root.parentElement, targetElement, updatePosition, children]);
    var positionerClass = classnames(positionerBlock, (_b = {},
        _b[positionerModifier('calculating')] = !hasCalculatedStyle,
        _b));
    return (React.createElement("div", { ref: positionerRef, className: positionerClass }, children));
};
